import * as React from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import {
    getQuizConfiguration,
    getQuizOptions,
    saveQuizConfiguration,
} from '../Api';
import QuizLeaguePicker from '../components/QuizLeaguePicker';
import SportEntityDto from '../models/dtos/SportEntityDto';
import { SaveQuizConfigurationRequest } from '../models/dtos/QuizAdminDtos';

export default function QuizConfigurationEditPage() {
    const { id } = useParams();
    const isEditMode = id != null && id !== 'create';
    const navigate = useNavigate();

    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [sports, setSports] = React.useState<SportEntityDto[]>([]);
    const [sportId, setSportId] = React.useState<number>(0);
    const [leagueIds, setLeagueIds] = React.useState<number[]>([]);
    /** Preserved from DB on edit — cron filter; not edited in this screen. */
    const [betGroupIds, setBetGroupIds] = React.useState<number[]>([]);
    const [deadlineHours, setDeadlineHours] = React.useState('24');
    const [isActive, setIsActive] = React.useState(true);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [dialogMessage, setDialogMessage] = React.useState('');

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            const options = await getQuizOptions();
            if (cancelled) return;
            setSports(options.sports ?? []);
            if (isEditMode && id) {
                const config = await getQuizConfiguration(Number(id));
                if (config) {
                    setSportId(config.sport?.id ?? 0);
                    setLeagueIds(config.leagueIds ?? []);
                    setBetGroupIds(config.betGroupIds ?? []);
                    setDeadlineHours(String(config.deadlineInHoursBeforeMatchStart ?? 24));
                    setIsActive(config.isActive);
                }
            } else if ((options.sports?.length ?? 0) > 0) {
                setSportId(options.sports[0].id);
            }
            setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [id, isEditMode]);

    const openDialog = (message: string) => {
        setDialogMessage(message);
        setDialogOpen(true);
    };

    const onSave = async () => {
        const deadline = parseInt(deadlineHours, 10);
        if (!sportId || sportId <= 0) {
            openDialog('Select a sport.');
            return;
        }
        if (leagueIds.length === 0) {
            openDialog('Select at least one league.');
            return;
        }
        if (Number.isNaN(deadline) || deadline <= 0) {
            openDialog('Deadline must be a positive number of hours.');
            return;
        }
        const payload: SaveQuizConfigurationRequest = {
            id: isEditMode ? Number(id) : null,
            sportId,
            leagueIds,
            betGroupIds,
            deadlineInHoursBeforeMatchStart: deadline,
            isActive,
        };
        setSaving(true);
        const saved = await saveQuizConfiguration(payload);
        setSaving(false);
        if (saved == null) {
            openDialog('Failed to save. Is betup-server-analytics deployed with quiz API?');
            return;
        }
        openDialog('Saved to qbet_quiz_configuration. Cron picks up active rows within ~30 minutes.');
    };

    if (loading) {
        return (
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, maxWidth: 720 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h4">
                    {isEditMode ? `Edit quiz config #${id}` : 'Add quiz sport & leagues'}
                </Typography>
                <Button onClick={() => navigate('/quiz/configurations')}>Back to list</Button>
            </Stack>

            <Alert severity="info" sx={{ mb: 3 }}>
                Saves to MariaDB table <strong>qbet_quiz_configuration</strong> (same as manual SQL).
                Active rows are read by cron every 30 min → creates rows in <strong>qbet_quiz</strong>.
                The app shows quizzes via v7 API — unchanged.
            </Alert>

            <Stack spacing={3}>
                <FormControl fullWidth>
                    <InputLabel id="quiz-sport-label">Sport</InputLabel>
                    <Select
                        labelId="quiz-sport-label"
                        label="Sport"
                        value={sportId || ''}
                        onChange={(e) => setSportId(Number(e.target.value))}
                    >
                        {sports.map(sport => (
                            <MenuItem key={sport.id} value={sport.id}>{sport.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Box>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>Leagues</Typography>
                    <QuizLeaguePicker
                        selectedIds={leagueIds}
                        onChange={setLeagueIds}
                        sportId={sportId || null}
                    />
                </Box>

                <TextField
                    label="Deadline before match start (hours)"
                    value={deadlineHours}
                    onChange={(e) => setDeadlineHours(e.target.value)}
                    helperText="Same field as cron: matches between now+deadline and now+deadline+24h."
                    fullWidth
                />

                <FormControlLabel
                    control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
                    label="Active (cron only uses active configurations)"
                />

                {isEditMode && betGroupIds.length > 0 && (
                    <Typography variant="body2" color="text.secondary">
                        Existing bet group filter in DB ({betGroupIds.length} ids) is preserved on save.
                    </Typography>
                )}

                <Button variant="contained" onClick={onSave} disabled={saving} sx={{ alignSelf: 'flex-start' }}>
                    {saving ? 'Saving…' : 'Save to database'}
                </Button>
            </Stack>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
                <DialogTitle>Quiz configuration</DialogTitle>
                <DialogContent>{dialogMessage}</DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>OK</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
